import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import Button from '../components/Button'
import LoadingSpinner from '../components/LoadingSpinner'
import api from '../services/api'

const CreateFacilitator = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [loadingTags, setLoadingTags] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [availableTags, setAvailableTags] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [newTagName, setNewTagName] = useState('')
  const [creatingTag, setCreatingTag] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    instagramId: '',
    linkedInUrl: '',
    bio: '',
    tags: [],
    phoneVisible: true,
    emailVisible: true,
    instagramVisible: true,
    linkedInVisible: true,
  })
  const [formErrors, setFormErrors] = useState({})

  useEffect(() => {
    loadTags()
  }, [])

  const loadTags = async () => {
    try {
      setLoadingTags(true)
      setError(null)
      const response = await api.get('/api/facilitators/tags')
      setAvailableTags(response.data.data)
      setLoadingTags(false)
    } catch (err) {
      console.error('Error loading tags:', err)
      setError('Failed to load tags. Please try again.')
      setLoadingTags(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: '',
      }))
    }
  }

  const handleTagToggle = (tagId) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter((id) => id !== tagId)
        : [...prev.tags, tagId],
    }))
  }

  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      return
    }

    try {
      setCreatingTag(true)
      setError(null)

      const response = await api.post('/api/facilitators/tags', {
        name: newTagName.trim(),
      })

      const newTag = response.data.data

      // Add new tag to available tags
      setAvailableTags((prev) => [...prev, newTag])

      // Add new tag to selected tags
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.id],
      }))

      // Clear input
      setNewTagName('')
      setCreatingTag(false)
    } catch (err) {
      console.error('Error creating tag:', err)
      setError(
        err.response?.data?.message || 'Failed to create tag. Please try again.'
      )
      setCreatingTag(false)
    }
  }

  const validateForm = () => {
    const errors = {}
    if (!formData.name.trim()) {
      errors.name = 'Name is required'
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)
      setError(null)
      setSuccess(false)

      await api.post('/api/facilitators', {
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phoneNumber || null,
        instagramId: formData.instagramId || null,
        linkedInUrl: formData.linkedInUrl || null,
        bio: formData.bio || null,
        tags: formData.tags,
        phoneVisible: formData.phoneVisible,
        emailVisible: formData.emailVisible,
        instagramVisible: formData.instagramVisible,
        linkedInVisible: formData.linkedInVisible,
      })

      setSuccess(true)
      setLoading(false)
      
      // Reset form after 2 seconds and redirect
      setTimeout(() => {
        navigate('/facilitators')
      }, 2000)
    } catch (err) {
      console.error('Error creating facilitator:', err)
      setError(
        err.response?.data?.message || 'Failed to create facilitator. Please try again.'
      )
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      name: '',
      email: '',
      phoneNumber: '',
      instagramId: '',
      linkedInUrl: '',
      bio: '',
      tags: [],
      phoneVisible: true,
      emailVisible: true,
      instagramVisible: true,
      linkedInVisible: true,
    })
    setFormErrors({})
    setError(null)
    setSuccess(false)
  }

  // Filter tags based on search query
  const filteredTags = availableTags.filter((tag) =>
    tag.name.toLowerCase().includes(searchQuery.toLowerCase())
  )
  const primaryTags = filteredTags.filter(
    (tag) => tag.tagType === 'primary'
  )
  const secondaryTags = filteredTags.filter(
    (tag) => tag.tagType === 'secondary' || !tag.tagType
  )

  if (loadingTags) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Loading tags...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-purple-50 to-pink-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Logo */}
        <div className="mb-6 flex justify-center">
          <img src="/logo.png" alt="Wellness Loop Logo" className="h-16 w-auto" />
        </div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Create New <span className="text-primary-600">Facilitator</span>
          </h1>
          <p className="text-lg text-gray-600">
            Enter facilitator information to add them to the directory
          </p>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl shadow-2xl p-8 md:p-12"
        >
          {success && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6"
            >
              <div className="flex items-center">
                <span className="text-2xl mr-2">✅</span>
                <span>Facilitator created successfully! Redirecting...</span>
              </div>
            </motion.div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* Basic Information Section */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Basic Information
                </h2>

                {/* Name */}
                <div className="mb-4">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${
                      formErrors.name
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                    placeholder="Enter facilitator name"
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email *
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="emailVisible"
                        checked={formData.emailVisible}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-600">Show in directory</span>
                    </label>
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${
                      formErrors.email
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                    placeholder="your.email@example.com"
                  />
                  {formErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                  )}
                </div>

                {/* Phone Number */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label
                      htmlFor="phoneNumber"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Phone Number
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="phoneVisible"
                        checked={formData.phoneVisible}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-600">Show in directory</span>
                    </label>
                  </div>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    placeholder="+1-555-0100"
                  />
                </div>
              </div>

              {/* Social Media Links Section */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Social Media Links
                </h2>

                {/* Instagram ID */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label
                      htmlFor="instagramId"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Instagram ID
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="instagramVisible"
                        checked={formData.instagramVisible}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-600">Show in directory</span>
                    </label>
                  </div>
                  <input
                    type="text"
                    id="instagramId"
                    name="instagramId"
                    value={formData.instagramId}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    placeholder="@yourusername"
                  />
                </div>

                {/* LinkedIn URL */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label
                      htmlFor="linkedInUrl"
                      className="block text-sm font-medium text-gray-700"
                    >
                      LinkedIn URL
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="linkedInVisible"
                        checked={formData.linkedInVisible}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-600">Show in directory</span>
                    </label>
                  </div>
                  <input
                    type="url"
                    id="linkedInUrl"
                    name="linkedInUrl"
                    value={formData.linkedInUrl}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>
              </div>

              {/* Bio Section */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Bio
                </h2>
                <div>
                  <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-2">
                    Biography
                  </label>
                  <textarea
                    id="bio"
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-gray-200 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                    placeholder="Tell us about the facilitator..."
                  />
                </div>
              </div>

              {/* Tags Section */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Select Tags
                </h2>
                <p className="text-gray-600 mb-6">
                  Choose tags that best describe the facilitator&apos;s expertise and offerings.
                </p>

                {/* Search Input */}
                <div className="mb-6">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search tags..."
                    className="w-full px-4 py-3 border-2 border-gray-200 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* Primary Tags Section */}
                {primaryTags.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">
                      Primary Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {primaryTags.map((tag) => (
                        <motion.button
                          key={tag.id}
                          type="button"
                          onClick={() => handleTagToggle(tag.id)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                            formData.tags.includes(tag.id)
                              ? 'bg-primary-600 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                          }`}
                        >
                          {tag.name}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Secondary Tags Section */}
                {secondaryTags.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">
                      Secondary Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {secondaryTags.map((tag) => (
                        <motion.button
                          key={tag.id}
                          type="button"
                          onClick={() => handleTagToggle(tag.id)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                            formData.tags.includes(tag.id)
                              ? 'bg-primary-600 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                          }`}
                        >
                          {tag.name}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )}

                {/* No tags found message */}
                {filteredTags.length === 0 && searchQuery && (
                  <p className="text-gray-500 text-sm mt-2">
                    No tags found matching &quot;{searchQuery}&quot;
                  </p>
                )}

                {filteredTags.length === 0 && !searchQuery && (
                  <p className="text-gray-500 text-sm mt-2">
                    No tags available
                  </p>
                )}

                {/* Add New Tag Section */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">
                    Add New Tag
                  </h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleCreateTag()
                        }
                      }}
                      placeholder="Enter tag name..."
                      className="flex-1 px-4 py-3 border-2 border-gray-200 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    />
                    <Button
                      type="button"
                      onClick={handleCreateTag}
                      variant="primary"
                      disabled={!newTagName.trim() || creatingTag}
                      className="px-6 py-3"
                    >
                      {creatingTag ? 'Adding...' : 'Add Tag'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
              <Button
                type="button"
                onClick={handleReset}
                variant="secondary"
                className="px-6 py-3"
                disabled={loading || success}
              >
                Reset
              </Button>
              <div className="flex gap-4">
                <Button
                  type="button"
                  onClick={() => navigate('/facilitators')}
                  variant="secondary"
                  className="px-6 py-3"
                  disabled={loading || success}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || success}
                  className="px-6 py-3"
                >
                  {loading ? 'Creating...' : 'Create Facilitator'}
                </Button>
              </div>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  )
}

export default CreateFacilitator

